<!-- Start the HSR Training page wrapper. -->
<div class="hsr-page">
  <!-- Start the HSR Training hero section. -->
  <section class="hero">
    <!-- Add the dark overlay used to keep hero text readable over the image. -->
    <div class="overlay"></div>
    <!-- Keep the hero content aligned with the site's standard container width. -->
    <div class="container hero-inner">
      <!-- Display the workplace health and safety category label. -->
      <span class="pill">Workplace Health &amp; Safety</span>
      <!-- Display the main HSR Training heading. -->
      <h1>HSR Training</h1>
      <div class="ecb-web-voc-title-line"></div>
      <!-- Display the full approved training-page title. -->
      <h2>Health and Safety Representative Training</h2>
      <!-- Explain the purpose and audience of the HSR training. -->
      <p>Professional, WorkSafe Victoria-approved training designed to help elected HSRs represent their colleagues with
        knowledge, confidence and practical skills.</p>
      <!-- Link prospective participants and organisations to the ECB contact page. -->
      <a routerLink="/contact-us" class="cta">Enquire About HSR Training <i class="bi bi-arrow-right"></i></a>
    </div>
  </section>

  <!-- Start the HSR Training introduction section. -->
  <section class="section intro">
    <!-- Use a two-column layout for the heading and detailed introduction. -->
    <div class="container split">
      <!-- Display the introductory heading. -->
      <div>
        <!-- Display the section label. -->
        <span class="kicker">What is HSR Training?</span>
        <!-- Display the section title with the highlighted phrase. -->
        <h2>Build confidence to represent a <span>safer workplace</span></h2>
      </div>
      <!-- Display the HSR explanation and WorkSafe logo note. -->
      <div>
        <!-- Explain the role of an elected Health and Safety Representative. -->
        <p>A Health and Safety Representative (HSR) is an elected worker who represents their designated work group on
          health and safety matters. Under the Occupational Health and Safety Act 2004 (Vic), elected HSRs have the
          legal right to attend WorkSafe-approved training.</p>
        <!-- Explain ECB's role as a WorkSafe Victoria-approved HSR training provider. -->
        <p>ECB Training Services is a WorkSafe Victoria-approved HSR training provider. Our training equips HSRs with
          the knowledge, skills, and confidence to effectively represent their colleagues and contribute to a safer
          workplace.</p>
        <!-- Display the implementation note for the official WorkSafe logo. -->
        <div class="logo-note">
          <!-- Display the approval-information icon. -->
          <i class="bi bi-shield-check"></i>
          <!-- Group the WorkSafe logo heading and explanation. -->
          <div>
            <!-- Explain that the official approved logo must be supplied by ECB. -->
            <strong>Approved WorkSafe logo required</strong>
            <!-- Explain why the logo has not been recreated in the implementation. -->
            <span>The brief requires the official “Approved Training Course” logo supplied by ECB. It has intentionally
              not been recreated or altered here.</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Start the HSR entitlements section. -->
  <section class="section entitlements">
    <!-- Keep the entitlements content aligned with the site's standard container width. -->
    <div class="container">
      <!-- Display the entitlements section heading. -->
      <div class="head">
        <!-- Display the legislation reference label. -->
        <span class="kicker">HSR Entitlements (Section 67 — OHS Act 2004)</span>
        <!-- Display the entitlements section title. -->
        <h2>HSR entitlements</h2>
        <!-- Introduce the entitlements listed below. -->
        <p>Under Section 67 of the Occupational Health and Safety Act 2004 (Victoria), elected HSRs are entitled to:</p>
      </div>
      <!-- Create the responsive grid for entitlement cards. -->
      <div class="ent-grid">
        <!-- Repeat one entitlement card for every entitlement defined in the component. -->
        <article *ngFor="let e of entitlements">
          <!-- Display the entitlement check icon. -->
          <i class="bi bi-check-circle-fill"></i>
          <!-- Display the entitlement description. -->
          <p>{{ e }}</p>
        </article>
      </div>
    </div>
  </section>

  <!-- Start the HSR course options section. -->
  <section class="section courses">
    <!-- Keep the course content aligned with the site's standard container width. -->
    <div class="container">
      <!-- Display the course section heading. -->
      <div class="head">
        <!-- Display the section label. -->
        <span class="kicker">Courses Offered</span>
        <!-- Display the course section title. -->
        <h2>Choose the right HSR course</h2>
      </div>
      <!-- Create the two-column layout for the initial and refresher courses. -->
      <div class="course-grid">
        <!-- Display the five-day initial HSR course card. -->
        <article>
          <!-- Display the initial course duration. -->
          <div class="duration">5 days</div>
          <!-- Display the initial course title. -->
          <h3>HSR Initial OHS Training Course</h3>
          <!-- Explain who the initial course is for and what it covers. -->
          <p>For newly elected Health and Safety Representatives. Covers the role and powers of an HSR, the OHS Act,
            hazard identification, risk assessment, and how to represent your work group effectively.</p>
          <!-- Link users to the contact page for an initial-course enquiry. -->
          <a routerLink="/contact-us">Enquire now <i class="bi bi-arrow-right"></i></a>
        </article>
        <!-- Display the one-day refresher HSR course card. -->
        <article>
          <!-- Display the refresher course duration. -->
          <div class="duration">1 day</div>
          <!-- Display the refresher course title. -->
          <h3>HSR Refresher OHS Training Course</h3>
          <!-- Explain who the refresher course is for and what it covers. -->
          <p>Annual refresher for HSRs who have completed initial training. Updates representatives on legislative
            changes, emerging issues, and reinforces core HSR skills and knowledge.</p>
          <!-- Link users to the contact page for a refresher-course enquiry. -->
          <a routerLink="/contact-us">Enquire now <i class="bi bi-arrow-right"></i></a>
        </article>
      </div>
    </div>
  </section>

  <!-- Start the Certificate of Attendance section. -->
  <section class="section receive">
    <!-- Use a two-column layout for the certificate requirements and visual card. -->
    <div class="container split align">
      <!-- Display the certificate requirement content. -->
      <div>
        <!-- Display the section label. -->
        <span class="kicker">What You Receive</span>
        <!-- Display the certificate section title. -->
        <h2>Certificate of Attendance</h2>
        <!-- Explain when the Certificate of Attendance is issued. -->
        <p>All participants receive a Certificate of Attendance upon successful completion. The certificate includes:
        </p>
        <!-- Display the required certificate information. -->
        <ul>
          <!-- Repeat one certificate requirement for every item defined in the component. -->
          <li *ngFor="let c of certificates">
            <!-- Display the requirement check icon. -->
            <i class="bi bi-check2"></i>
            <!-- Display the certificate requirement text. -->
            {{ c }}
          </li>
        </ul>
      </div>
      <!-- Display a generic certificate visual without recreating the restricted WorkSafe logo. -->
      <div class="certificate-visual">
        <!-- Display the certificate award icon. -->
        <i class="bi bi-award"></i>
        <!-- Display the certificate visual heading. -->
        <h3>Certificate of Attendance</h3>
        <!-- Explain when the certificate is issued. -->
        <p>Issued following successful completion of the approved course.</p>
        <!-- Display ECB as the training provider. -->
        <span>ECB Training Services</span>
      </div>
    </div>
  </section>

  <!-- Start the HSR training schedule section. -->
  <section class="schedule">
    <!-- Create the schedule introduction and details layout. -->
    <div class="container schedule-grid">
      <!-- Display the schedule introduction. -->
      <div>
        <!-- Display the schedule section label. -->
        <span class="kicker light">Training Schedule and Upcoming Dates</span>
        <!-- Display the schedule section title. -->
        <h2>Upcoming HSR training</h2>
        <!-- Tell users how to confirm current dates or arrange a group booking. -->
        <p>Contact ECB to confirm the current schedule or arrange a group booking.</p>
      </div>
      <!-- Display the current schedule details card. -->
      <div class="schedule-card">
        <!-- Display the delivery-location information. -->
        <div>
          <!-- Display the delivery-location label. -->
          <span>Delivery Location</span>
          <!-- Display the delivery-location value. -->
          <strong>Darwin, NT (primary) · Additional locations by arrangement</strong>
        </div>
        <!-- Display the delivery-format information. -->
        <div>
          <!-- Display the format label. -->
          <span>Format</span>
          <!-- Display the format value. -->
          <strong>Face-to-face — small group delivery</strong>
        </div>
        <!-- Display the upcoming-dates information. -->
        <div>
          <!-- Display the upcoming-dates label. -->
          <span>Upcoming dates</span>
          <!-- Display the upcoming-dates value. -->
          <strong>Contact ECB to confirm current schedule</strong>
        </div>
        <!-- Display the group-booking information. -->
        <div>
          <!-- Display the group-bookings label. -->
          <span>Group bookings</span>
          <!-- Display the group-bookings value. -->
          <strong>Available for organisations with multiple HSRs</strong>
        </div>
        <!-- Link users to the ECB contact page for HSR enquiries and bookings. -->
        <!-- <a routerLink="/contact-us" class="cta">Contact ECB <i class="bi bi-arrow-right"></i></a> -->
      </div>
    </div>
  </section>
</div>
<!-- End the HSR Training page wrapper. -->